import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { IndeterminateCheckbox } from 'src/common/primitives/IndeterminateCheckbox';

import { dispatcherSlice } from '../dispatcher.slice';
import { TripListItem } from '../trips/TripListItem';

export function MultiloadAssignmentSelection() {
  const dispatch = useAppDispatch();
  const unassignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.unassigned_trips
  );
  const assignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.assigned_trips
  );
  const {
    originatingTripId,
    selectedTripIdsToMultiload,
    potentialMultiloadTrips,
    existingMultiloadTripIds,
    mode,
  } = useAppSelector((state) => state.dispatcher.multiloadAssignmentFlow);

  if (!(unassignedTrips && assignedTrips)) {
    return <div>No Trips Found</div>;
  }

  const isEditMode = mode === 'edit-trip-multi-load';

  // In assign/create modes potentialMultiloadTrips excludes originatingTripId;
  // in edit mode it includes all trips in the existing multi-load.
  // Exclude existing multiload trips since they are locked and not freely selectable.
  const selectableTripIds = potentialMultiloadTrips.filter(
    (id) => !existingMultiloadTripIds.includes(id)
  );

  const allSelected =
    selectableTripIds.length > 0 &&
    selectableTripIds.every((id) => selectedTripIdsToMultiload.includes(id));

  const someSelected = selectableTripIds.some((id) =>
    selectedTripIdsToMultiload.includes(id)
  );

  function handleSelectAll(checked: boolean) {
    dispatch(dispatcherSlice.actions.onMultiloadSelectAll(checked));
  }

  return (
    <>
      {selectableTripIds.length > 0 && (
        <label className="flex items-center gap-2 pb-2">
          <IndeterminateCheckbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={handleSelectAll}
          />
          <span>Select all</span>
        </label>
      )}
      <ul className="flex flex-col gap-y-3">
        {[...unassignedTrips, ...assignedTrips]
          .filter(
            (t) =>
              potentialMultiloadTrips.includes(t.id) ||
              t.id === originatingTripId
          )
          // Sort the trips so that the originating trip is always at the top
          .sort((a, b) => {
            if (a.id === originatingTripId) return -1;
            if (b.id === originatingTripId) return 1;
            return 0;
          })
          .map((trip) => (
            <li key={trip.id}>
              <TripListItem
                trip={trip}
                disabled={
                  (!isEditMode && originatingTripId === trip.id) ||
                  existingMultiloadTripIds.includes(trip.id)
                }
                checked={selectedTripIdsToMultiload.includes(trip.id)}
                onCheckedChange={(val) => {
                  dispatch(
                    dispatcherSlice.actions.onMultiloadAssignmentCheckedChange({
                      tripId: trip.id,
                      checked: val === true,
                    })
                  );
                }}
                hideMenu
              />
              {existingMultiloadTripIds.includes(trip.id) && (
                <p className="text-xs text-gray-500 ml-9 mt-1">
                  Already in a multi-load
                </p>
              )}
            </li>
          ))}
      </ul>
    </>
  );
}
